import { Pencil, Trash2, AlertTriangle, Folder, User } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import type { Task, TaskStatus } from '../types';

// ─── Priority badge colours (design-system tokens) ─────────────────────────
const PRIORITY_CLASSES: Record<string, string> = {
  low: 'bg-surface-variant text-on-surface-variant',
  medium: 'bg-info/10 text-info',
  high: 'bg-warning/10 text-warning',
  critical: 'bg-danger/10 text-danger',
};

interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onOpenDrawer: (task: Task) => void;
  onStatusChange: (id: string, status: TaskStatus) => void;
}

function isOverdue(task: Task): boolean {
  return !!(
    task.due_date &&
    task.status !== 'done' &&
    new Date(task.due_date) < new Date()
  );
}

function fmtShortDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export function TaskCard({ task, onEdit, onDelete, onOpenDrawer, onStatusChange }: TaskCardProps) {
  const overdue = isOverdue(task);

  return (
    <div className="group relative cursor-default rounded-card border border-outline bg-surface p-3 transition-all hover:border-primary/30 hover:shadow-surface-1">
      {/* Title row */}
      <div className="flex items-start gap-2">
        <button
          className="flex-1 text-left text-sm font-semibold leading-snug text-on-surface hover:text-primary"
          onClick={() => onOpenDrawer(task)}
          title="View details"
        >
          {task.title}
        </button>

        {/* Hover actions */}
        <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={() => onEdit(task)}
            className="rounded p-1 text-on-surface-variant hover:bg-surface-variant hover:text-primary"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(task)}
            className="rounded p-1 text-on-surface-variant hover:bg-danger/10 hover:text-danger"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Project */}
      {task.project_name && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-on-surface-variant">
          <Folder className="h-3 w-3" />
          <span className="truncate">{task.project_name}</span>
        </div>
      )}

      {/* Assignee */}
      {task.assignee_name && (
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-on-surface-variant">
          <User className="h-3 w-3" />
          <span className="truncate">{task.assignee_name}</span>
        </div>
      )}

      {/* Priority + Due */}
      <div className="mt-2 flex items-center justify-between">
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
            PRIORITY_CLASSES[task.priority] ?? PRIORITY_CLASSES.medium,
          )}
        >
          {task.priority}
        </span>
        {task.due_date && (
          <span
            className={cn(
              'text-[11px]',
              overdue ? 'font-bold text-danger' : 'text-on-surface-variant',
            )}
          >
            {overdue && <AlertTriangle className="mr-0.5 inline h-3 w-3" />}
            {fmtShortDate(task.due_date)}
          </span>
        )}
      </div>

      {/* Quick status change (visible on hover) */}
      <select
        className="mt-2 w-full cursor-pointer rounded-card-sm border border-outline bg-surface-variant px-1.5 py-1 text-[10px] text-on-surface-variant opacity-0 outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-primary/40 group-hover:opacity-100"
        value={task.status}
        onChange={(e) => onStatusChange(task.id, e.target.value as TaskStatus)}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Change status for ${task.title}`}
      >
        <option value="todo">To Do</option>
        <option value="in_progress">In Progress</option>
        <option value="review">Review</option>
        <option value="done">Done</option>
      </select>
    </div>
  );
}
